import { useState, useRef, useEffect } from "react";

const monthlyUsage = [
  { month: "10月", usage: 22.1, cost: 76.25, avg: 20.5 },
  { month: "11月", usage: 28.5, cost: 98.33, avg: 26.2 },
  { month: "12月", usage: 38.9, cost: 134.21, avg: 35.8 },
  { month: "1月", usage: 34.5, cost: 119.03, avg: 32.1 },
  { month: "2月", usage: 29.7, cost: 102.47, avg: 27.9 },
  { month: "3月", usage: 25.9, cost: 89.36, avg: 24.3 },
];

const hourlyUsage = [
  { hour: "0", val: 0.2 }, { hour: "1", val: 0.1 }, { hour: "2", val: 0.1 }, { hour: "3", val: 0.1 },
  { hour: "4", val: 0.2 }, { hour: "5", val: 0.3 }, { hour: "6", val: 1.8 }, { hour: "7", val: 2.4 },
  { hour: "8", val: 1.2 }, { hour: "9", val: 0.5 }, { hour: "10", val: 0.4 }, { hour: "11", val: 1.6 },
  { hour: "12", val: 2.1 }, { hour: "13", val: 0.6 }, { hour: "14", val: 0.3 }, { hour: "15", val: 0.3 },
  { hour: "16", val: 0.4 }, { hour: "17", val: 0.8 }, { hour: "18", val: 2.8 }, { hour: "19", val: 2.2 },
  { hour: "20", val: 1.4 }, { hour: "21", val: 0.9 }, { hour: "22", val: 0.5 }, { hour: "23", val: 0.3 },
];

const usageCategories = [
  { label: "烹饪用气", pct: 58, color: "#E8531A", icon: "ri-restaurant-line" },
  { label: "热水器", pct: 28, color: "#f97316", icon: "ri-temp-hot-line" },
  { label: "暖气", pct: 10, color: "#fbbf24", icon: "ri-fire-fill" },
  { label: "其他", pct: 4, color: "#d1d5db", icon: "ri-more-line" },
];

const aiTips = [
  "您的早高峰（7-8点）用气量较高，建议错峰使用，可节省约8%用气量",
  "本月用气量较上月减少3.8 m³，节省费用约¥13.1，继续保持！",
  "您的用气效率评分为 B+，与同类用户相比处于中上水平",
  "建议检查热水器设定温度，将水温从60°C调至55°C可节省约12%燃气",
];

interface EnergyAnalysisProps {
  accentColor?: string;
}

export default function EnergyAnalysis({ accentColor = "#E8531A" }: EnergyAnalysisProps) {
  const [subTab, setSubTab] = useState<"monthly" | "hourly" | "category">("monthly");
  const barCanvasRef = useRef<HTMLCanvasElement>(null);
  const hourCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (subTab !== "monthly") return;
    const canvas = barCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxVal = Math.max(...monthlyUsage.map(d => Math.max(d.usage, d.avg))) + 5;
    const barW = (W - 32) / monthlyUsage.length;
    monthlyUsage.forEach((d, i) => {
      const x = 16 + i * barW;
      // Avg line dot
      const avgY = H - 20 - ((d.avg / maxVal) * (H - 30));
      // Bar
      const barH = ((d.usage / maxVal) * (H - 30));
      const bx = x + barW * 0.15;
      const bw = barW * 0.55;
      const by = H - 20 - barH;
      const grad = ctx.createLinearGradient(bx, by, bx, H - 20);
      grad.addColorStop(0, i === 5 ? accentColor : `${accentColor}cc`);
      grad.addColorStop(1, `${accentColor}22`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(bx, by, bw, barH, 3);
      ctx.fill();
      // Avg dot
      ctx.beginPath();
      ctx.arc(x + barW * 0.42, avgY, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = "#94a3b8";
      ctx.fill();
      // Label
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(d.month, x + barW * 0.42, H - 5);
    });
    // Connect avg dots
    ctx.beginPath();
    monthlyUsage.forEach((d, i) => {
      const x = 16 + i * barW + barW * 0.42;
      const y = H - 20 - ((d.avg / maxVal) * (H - 30));
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#94a3b8";
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }, [subTab, accentColor]);

  useEffect(() => {
    if (subTab !== "hourly") return;
    const canvas = hourCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const maxVal = Math.max(...hourlyUsage.map(d => d.val)) + 0.3;
    const barW = (W - 16) / hourlyUsage.length;
    hourlyUsage.forEach((d, i) => {
      const barH = ((d.val / maxVal) * (H - 20));
      const x = 8 + i * barW + barW * 0.1;
      const bw = barW * 0.8;
      const by = H - 16 - barH;
      const isHigh = d.val >= 2.0;
      const grad = ctx.createLinearGradient(x, by, x, H - 16);
      grad.addColorStop(0, isHigh ? accentColor : `${accentColor}66`);
      grad.addColorStop(1, `${accentColor}11`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, by, bw, barH, 2);
      ctx.fill();
      if (i % 4 === 0) {
        ctx.fillStyle = "#9ca3af";
        ctx.font = "8px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(`${d.hour}h`, x + bw / 2, H - 3);
      }
    });
  }, [subTab, accentColor]);

  const totalUsage = monthlyUsage.reduce((s, d) => s + d.usage, 0);
  const totalCost = monthlyUsage.reduce((s, d) => s + d.cost, 0);
  const avgMonthly = totalUsage / monthlyUsage.length;

  return (
    <div className="p-4 space-y-4">
      <div className="text-sm font-semibold text-gray-800">用能分析</div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "近6月用气", value: `${totalUsage.toFixed(1)}`, unit: "m³", color: "text-[#E8531A]" },
          { label: "近6月费用", value: `¥${totalCost.toFixed(0)}`, unit: "", color: "text-amber-600" },
          { label: "月均用气", value: `${avgMonthly.toFixed(1)}`, unit: "m³", color: "text-teal-600" },
        ].map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
            <div className={`text-base font-bold ${s.color}`}>{s.value}<span className="text-xs font-normal text-gray-400 ml-0.5">{s.unit}</span></div>
            <div className="text-[10px] text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {[
          { key: "monthly", label: "月度趋势" },
          { key: "hourly", label: "时段分布" },
          { key: "category", label: "用途分析" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSubTab(t.key as typeof subTab)}
            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${subTab === t.key ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Monthly trend */}
      {subTab === "monthly" && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-700">月度用气量对比</span>
            <div className="flex items-center gap-3 text-[10px] text-gray-400">
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-sm" style={{ background: accentColor }}></div>实际</div>
              <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-gray-400"></div>同类均值</div>
            </div>
          </div>
          <canvas ref={barCanvasRef} width={320} height={110} className="w-full" />
          <div className="mt-3 space-y-1.5">
            {monthlyUsage.slice(-3).map((d, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-gray-500">{d.month}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-700">{d.usage} m³</span>
                  <span className="text-gray-400">¥{d.cost.toFixed(2)}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${d.usage > d.avg ? "bg-amber-50 text-amber-600" : "bg-green-50 text-green-600"}`}>
                    {d.usage > d.avg ? `+${(d.usage - d.avg).toFixed(1)}` : `-${(d.avg - d.usage).toFixed(1)}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hourly distribution */}
      {subTab === "hourly" && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-700">24小时用气分布</span>
            <span className="text-[10px] text-gray-400">今日</span>
          </div>
          <canvas ref={hourCanvasRef} width={320} height={100} className="w-full" />
          <div className="mt-3 grid grid-cols-2 gap-2">
            {[
              { label: "早高峰", time: "07:00-08:00", val: "2.4 m³", color: "text-[#E8531A]" },
              { label: "午高峰", time: "12:00-13:00", val: "2.1 m³", color: "text-amber-600" },
              { label: "晚高峰", time: "18:00-19:00", val: "2.8 m³", color: "text-[#E8531A]" },
              { label: "低谷时段", time: "01:00-05:00", val: "0.1 m³", color: "text-green-600" },
            ].map((p, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2.5">
                <div className={`text-xs font-semibold ${p.color}`}>{p.label}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">{p.time}</div>
                <div className="text-xs text-gray-700 font-medium mt-0.5">{p.val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category analysis */}
      {subTab === "category" && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="text-xs font-semibold text-gray-700 mb-3">用气用途分析</div>
          <div className="space-y-3">
            {usageCategories.map((cat, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: `${cat.color}15` }}>
                      <i className={`${cat.icon} text-xs`} style={{ color: cat.color }}></i>
                    </div>
                    <span className="text-xs text-gray-700">{cat.label}</span>
                  </div>
                  <span className="text-xs font-bold text-gray-800">{cat.pct}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${cat.pct}%`, background: cat.color }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 bg-orange-50 rounded-xl p-3">
            <div className="text-xs font-semibold text-[#E8531A] mb-1">AI节能建议</div>
            <div className="text-xs text-gray-600 leading-relaxed">
              您的烹饪用气占比58%，建议使用节能灶具，可降低约15%用气量。热水器温度建议设置55°C，每月可节省约¥8-12。
            </div>
          </div>
        </div>
      )}

      {/* AI Tips */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-[#E8531A] flex items-center justify-center">
            <i className="ri-robot-2-line text-white text-xs"></i>
          </div>
          <span className="text-xs font-semibold text-gray-800">AI用能洞察</span>
        </div>
        <div className="space-y-2">
          {aiTips.map((tip, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#E8531A] text-[9px] font-bold">{i + 1}</span>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
